/**
 * Phase 3.28 — Verkaufs-Sub-Sections.
 * BuyRequestAcceptanceSection (Ankäufe), SalePreferencesSection (Zielgruppen),
 * ProgramParticipationSection (Teilnahme an PreisTalk u.a.).
 */
import { useEffect, useState } from "react";
import { Globe, Handshake, ShieldCheck, ShieldQuestion, ShieldOff } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { CheckRow } from "./_shared";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

export function BuyRequestAcceptanceSection({
    currentUser,
    embedded = false,
    controlled = null, // { enabled, termsAccepted, onEnabledChange, onTermsChange }
}) {
    const { refresh } = useAuth();
    const isControlled = !!controlled;
    const [enabledLocal, setEnabledLocal] = useState(!!currentUser.accept_buy_requests);
    const [termsAcceptedLocal, setTermsAcceptedLocal] = useState(!!currentUser.buy_request_terms_accepted_at);
    const [saving, setSaving] = useState(false);

    const enabled = isControlled ? controlled.enabled : enabledLocal;
    const termsAccepted = isControlled ? controlled.termsAccepted : termsAcceptedLocal;
    const setEnabled = isControlled ? controlled.onEnabledChange : setEnabledLocal;
    const setTermsAccepted = isControlled ? controlled.onTermsChange : setTermsAcceptedLocal;

    const dirty =
        enabledLocal !== !!currentUser.accept_buy_requests
        || termsAcceptedLocal !== !!currentUser.buy_request_terms_accepted_at;

    const save = async () => {
        if (enabledLocal && !termsAcceptedLocal) {
            toast.error("Bitte zuerst die Annahmebedingungen bestätigen.");
            return;
        }
        setSaving(true);
        try {
            await api.patch("/users/me/dealer-profile", {
                buy_request_terms_accepted_at: termsAcceptedLocal
                    ? (currentUser.buy_request_terms_accepted_at || new Date().toISOString())
                    : null,
            });
            await api.patch("/users/me/preferences", { accept_buy_requests: enabledLocal });
            await refresh();
            toast.success("Einstellungen gespeichert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    const outerCls = embedded
        ? "mt-6 pt-6 border-t border-gray-100"
        : "bg-white border border-gray-200 rounded-sm p-6";

    return (
        <div className={outerCls} data-testid="buy-request-acceptance-section">
            <label
                className="flex items-start gap-3 p-3 rounded-sm border border-gray-200 hover:bg-[#FFFBEB]/40 cursor-pointer"
                data-testid="accept-buy-requests-toggle"
            >
                <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    className="mt-1 w-5 h-5 accent-[#FFCC00]"
                    data-testid="accept-buy-requests-checkbox"
                />
                <div className="flex-1">
                    <div className="font-bold text-[#111827] flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-[#FFCC00]" />
                        An Geboten auf B2C (private) Verkaufsgesuche teilnehmen
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                        Wenn aktiviert, sehen Sie Ankaufanfragen von Privatpersonen in „Privatkunden-Ankäufe"
                        und können selbständig Angebote abgeben. Anfragen werden über Ihre <strong>Top-5 Lieblingsmarken</strong> gefiltert (siehe Händlerprofil); ohne Lieblingsmarken sehen Sie alle Anfragen.
                    </div>
                </div>
            </label>

            {enabled && (
                <div className="mt-3 ml-6 border-l-2 border-[#FFCC00] pl-4" data-testid="buy-request-terms-block">
                    <label className="flex items-start gap-3 cursor-pointer py-2" data-testid="buy-request-terms-toggle">
                        <input
                            type="checkbox"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            className="mt-0.5 w-4 h-4 accent-[#FFCC00]"
                            data-testid="buy-request-terms-checkbox"
                        />
                        <div className="flex-1">
                            <div className="text-sm font-bold text-[#111827]">
                                Ich stimme den{" "}
                                <a
                                    href="/legal/buy-request-terms"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-700 underline hover:text-blue-900"
                                    data-testid="buy-request-terms-link"
                                >
                                    Annahmebedingungen
                                </a>{" "}
                                zu
                            </div>
                            <div className="text-[11px] text-gray-500 mt-0.5">
                                Pflichtfeld zur Aufnahme als ankaufender Händler — bestätigt, dass Sie
                                Fahrzeuge mit gültigen Papieren prüfen und faire Angebote abgeben.
                            </div>
                            {termsAccepted && currentUser.buy_request_terms_accepted_at && (
                                <div className="text-[10px] text-gray-400 mt-1" data-testid="buy-request-terms-accepted-at">
                                    Bestätigt am: {String(currentUser.buy_request_terms_accepted_at).slice(0, 10)}
                                </div>
                            )}
                        </div>
                    </label>
                </div>
            )}

            {!isControlled && (
                <Button
                    onClick={save}
                    disabled={saving || !dirty}
                    className="mt-4 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider w-full disabled:opacity-50"
                    data-testid="save-buy-request-acceptance-btn"
                >
                    {saving ? "Speichert…" : "Einstellungen speichern"}
                </Button>
            )}
        </div>
    );
}


// ============================================================================
// SALE PREFERENCES extension (sell_to only — Programs moved to inline block in
// the main "Verkaufseinstellungen" card via ProgramParticipationSection)
// ============================================================================
export function SalePreferencesSection({ currentUser }) {
    const { refresh } = useAuth();
    const [busy, setBusy] = useState(false);

    const flip = async (key, next) => {
        setBusy(true);
        try {
            await api.patch("/users/me/preferences", { [key]: next });
            await refresh();
            toast.success("Gespeichert");
        } catch {
            toast.error("Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="sale-prefs-section">
            <h3 className="font-display font-bold text-lg mb-1">Zielgruppen</h3>
            <p className="text-xs text-gray-600 mb-4">An welche Zielgruppen und Regionen verkaufen Sie?</p>

            <CheckRow label="B2B – Gewerbe" hint="Andere Händler dürfen Anfragen senden" checked={currentUser.sell_to_b2b !== false} disabled={busy} onChange={(v) => flip("sell_to_b2b", v)} testId="sell-b2b-checkbox" />
            <CheckRow label="B2C – Privat" hint="Privatkunden dürfen Anfragen senden" checked={currentUser.sell_to_b2c !== false} disabled={busy} onChange={(v) => flip("sell_to_b2c", v)} testId="sell-b2c-checkbox" />
            <CheckRow label="Regional (Umkreis 150 km)" hint="Anfragen nur aus dem Umkreis von 150 km Ihres Firmenstandorts" checked={!!currentUser.sell_regional} disabled={busy} onChange={(v) => flip("sell_regional", v)} testId="sell-regional-checkbox" />
            <CheckRow label="Europaweit" hint="Anfragen aus der gesamten EU & EWR zulassen" checked={!!currentUser.sell_eu} disabled={busy} onChange={(v) => flip("sell_eu", v)} testId="sell-eu-checkbox" />
            <CheckRow label="International" hint="Anfragen weltweit zulassen (Export, Versandabwicklung erforderlich)" checked={!!currentUser.sell_international} disabled={busy} onChange={(v) => flip("sell_international", v)} testId="sell-international-checkbox" />
        </div>
    );
}

// ============================================================================
// PROGRAM PARTICIPATION — rendered INSIDE the main "Verkaufseinstellungen" card
// at the bottom (no own card chrome). bikeVinci has been moved entirely to
// "Server-Adressen für Integrationen", so it's referenced via a hint only.
// ============================================================================
export function ProgramParticipationSection({ currentUser, controlled = null }) {
    const { refresh } = useAuth();
    const [busy, setBusy] = useState(false);
    const isControlled = !!controlled;

    const valueFor = (key) => isControlled ? !!controlled.values[key] : !!currentUser[key];

    const flip = async (key, next) => {
        if (isControlled) {
            controlled.onChange(key, next);
            return;
        }
        setBusy(true);
        try {
            await api.patch("/users/me/preferences", { [key]: next });
            await refresh();
            toast.success("Gespeichert");
        } catch {
            toast.error("Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-6 pt-6 border-t border-gray-100" data-testid="program-participation-section">
            <h4 className="font-bold text-sm uppercase tracking-wider text-gray-700 mb-1">PreisTalk / Lead Generator</h4>
            <p className="text-xs text-gray-600 mb-2">Vormerkung für kommende Programme — Sie werden informiert sobald sie live gehen.</p>
            <CheckRow label="Pfand / Refinanzierung" hint="Vormerkung – Sie werden informiert sobald das Programm live geht." checked={valueFor("program_pfand")} disabled={busy} onChange={(v) => flip("program_pfand", v)} testId="prog-pfand-checkbox" />
            <CheckRow label="Sharing / Vermietung" hint="Vormerkung – Sharing-Plattform-Anbindung in Vorbereitung." checked={valueFor("program_sharing")} disabled={busy} onChange={(v) => flip("program_sharing", v)} testId="prog-sharing-checkbox" />
            <CheckRow label="PreisTalk / Lead Generator" hint="Vormerkung – PreisTalk-Verhandlungstool & Lead-Generator in Vorbereitung." checked={valueFor("program_preistalk")} disabled={busy} onChange={(v) => flip("program_preistalk", v)} testId="prog-preistalk-checkbox" />
        </div>
    );
}

// ============================================================================
// BENACHRICHTIGUNGS-ROUTING (Phase 1)
// Pro Kategorie (messages, offers, tradein, buyrequests):
//   E-Mail Toggle + editierbare Adresse · SMS Toggle + editierbare Nummer
// Wird IMMER controlled vom Parent betrieben (kein eigener API-Call).
// ============================================================================
// ============================================================================
// MESSAGES NOTIFICATION CARD — extracted so it can be rendered standalone
// under the "Direkt-Chat erlauben" checkbox in DashboardPage (Phase 3.21).
// Mirrors the per-category UI inside NotificationsSection.
// ============================================================================
// Phase 3.27 — MessagesNotificationCard + NotificationsSection in /components/dealer/NotificationsSection.jsx ausgelagert.
export { MessagesNotificationCard, NotificationsSection } from "./NotificationsSection";



// ============================================================================
// BOERSEN-EXPORTE
// ============================================================================
// Phase 3.27 — ExchangeExportSection in /components/dealer/ExchangeExportSection.jsx ausgelagert.
export { ExchangeExportSection } from "./ExchangeExportSection";

// ============================================================================
// SCHNITTSTELLEN — Phase 3.26: Vollständig in /components/dealer/VehicleSections.jsx
// ausgelagert. Hier nur Re-Exports für Rückwärtskompatibilität.
// ============================================================================
export { VehicleExportSection, VehicleImportSection } from "./VehicleSections";
import { VehicleExportSection as _VehicleExportSection, VehicleImportSection as _VehicleImportSection } from "./VehicleSections";

// Legacy alias — kept for any external imports (admin pages, tests).
